import Link from 'next/link'
import { useSearchParams } from 'next/navigation'
import { useState } from 'react'
import { Button } from 'src/components/Button'
import { TextField } from 'src/components/Fields'
import LoadingSpinner from 'src/components/LoadingSpinner'
import OauthButtons from 'src/components/OauthButtons'
import { SlimLayout } from 'src/components/SlimLayout'
import type { OAuthKinds } from 'src/utils/api'
import { addToWaitlist, authorize, getSiteUrlPrefix, OAuthScopes, track } from 'src/utils/api'
import { getHomeUrl } from 'src/utils/url'

type PendingMethod = OAuthKinds | 'form' | undefined

const WaitlistSignUp = () => {
    const searchParams = useSearchParams()
    const [fullName, setFullName] = useState('')
    const [email, setEmail] = useState('')
    const [pendingMethod, setPendingMethod] = useState<PendingMethod>(undefined)
    const [joined, setJoined] = useState(searchParams.get('waitlist_status') === 'joined')
    const [error, setError] = useState<string | undefined>(
        searchParams.get('waitlist_error') === 'oauth'
            ? 'We could not add that account to the waitlist. Please enter your details below.'
            : undefined,
    )
    const submitting = pendingMethod !== undefined
    const canSubmit = fullName.trim() !== '' && email.trim() !== '' && !submitting

    const submit = async (event: React.FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setPendingMethod('form')
        setError(undefined)
        await track('waitlistStarted', { kind: 'email' }).catch(() => undefined)
        try {
            await addToWaitlist({
                email: email.trim(),
                fullName: fullName.trim(),
                areaOfInterest: 'WAITLIST_AREA_OF_INTEREST_JOIN_WAITLIST',
            })
            setJoined(true)
        } catch (err) {
            setError('Something went wrong. Please try again.')
        } finally {
            setPendingMethod(undefined)
        }
    }

    const createOauthWaitlist = (kind: 'google' | 'github') => async () => {
        setPendingMethod(kind)
        setError(undefined)
        await track('waitlistStarted', { kind }).catch(() => undefined)
        try {
            const returnTo = `${getSiteUrlPrefix()}/signup?waitlist_status=joined`
            const { redirectUrl } = await authorize(kind, OAuthScopes[kind], {
                reason: 'waitlist',
                returnTo,
            })
            if (redirectUrl === undefined) {
                throw new Error(`OAuth provider '${kind}' did not return a redirect URL`)
            }
            window.location.href = redirectUrl
        } catch (err) {
            setError('Something went wrong. Please try again.')
            setPendingMethod(undefined)
        }
    }

    return (
        <SlimLayout>
            {/* Home is served by Cloudflare Pages; full page load required. */}
            <a
                className="absolute top-4 left-4 flex flex-row items-center gap-1 h-[18px] cursor-pointer"
                href={getHomeUrl()}
            >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M11 14L5 8L11 2" stroke="#89919D" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <span className="text-sm text-[#3B4049]">Back</span>
            </a>
            <div className="pt-12 pb-8 w-full">
                <p className="text-xl mb-3 text-neutral-900 font-bold">Join the waitlist</p>
                <p className="text-xs mb-6 text-neutral-700">
                    Be the first to hear about product updates and early access opportunities.
                </p>
                {joined ? (
                    <p className="text-sm font-medium text-neutral-900 py-4">
                        Thanks for joining our waitlist, we&apos;ll keep you posted!
                    </p>
                ) : (
                    <>
                        <p className="text-xs mb-3 text-neutral-900">Join with:</p>
                        <OauthButtons
                            signinGoogle={createOauthWaitlist('google')}
                            signinGitHub={createOauthWaitlist('github')}
                            disabled={submitting}
                            pendingKind={
                                pendingMethod === 'google' || pendingMethod === 'github' ? pendingMethod : undefined
                            }
                        />
                        <div className="flex items-center w-full my-4">
                            <div className="flex-grow h-px bg-gray-300"></div>
                            <div className="px-4 text-xs text-gray-500">Or</div>
                            <div className="flex-grow h-px bg-gray-300"></div>
                        </div>
                        <form onSubmit={submit} className="grid grid-cols-1 gap-y-3">
                            <TextField
                                label="Full name"
                                type="text"
                                placeholder="Enter your full name"
                                autoComplete="name"
                                value={fullName}
                                onChange={event => setFullName(event.target.value)}
                                disabled={submitting}
                                required
                            />
                            <TextField
                                label="Email"
                                type="email"
                                placeholder="Enter your email address"
                                autoComplete="email"
                                value={email}
                                onChange={event => setEmail(event.target.value)}
                                disabled={submitting}
                                required
                            />
                            <Button
                                type="submit"
                                variant="solid"
                                color="blue"
                                className="mt-2 w-full bg-primary-500 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
                                disabled={!canSubmit}
                            >
                                <LoadingSpinner isLoading={pendingMethod === 'form'}>
                                    <span>Join the waitlist</span>
                                </LoadingSpinner>
                            </Button>
                            {error !== undefined && <p className="text-red-500 text-sm text-center">{error}</p>}
                        </form>
                    </>
                )}
                <p className="text-center mt-3 text-xs text-neutral-700">
                    Already have an account?{' '}
                    <Link href="/signin" className="font-medium text-primary-600 hover:underline">
                        Sign in
                    </Link>
                </p>
            </div>
        </SlimLayout>
    )
}

export default WaitlistSignUp
